/**
 * The live per-channel acquisition figures (ad-spend today and blended CPA) keyed by the
 * channel `aft`, sourced from the same real endpoints the acquisition cards and the
 * channel-detail page read. Extracted from {@link useAcquisitionChannels} so BOTH the
 * "Lumio Channels" cards (via that hook) AND the "Ad Budget & Daily Limits" modal
 * (`AdBudgetLimit`, which self-sources because it is mounted from two places) share ONE
 * fetch + ONE derivation, no duplicate network calls, no drift between card and modal.
 * The pure per-channel maths live in `channel-figures.ts` ({@link buildChannelFigure}).
 *
 *  - **spent** = the channel's ad-spend today, summed from the Casino Ad Service campaign
 *    `performance.totals.cost` over the channel's `network` campaigns.
 *  - **cpaValue** = `cost / FTDs` (FTDs from `daily_affiliate_activity` grouped on `aft`), or
 *    `null` when the request errored or no FTD landed today.
 *  - **cpaLabel** = the same display string the cards show (`"$8 CPA"` / `"– CPA"` / …).
 *
 * Live only for enabled channels (only Web today); the Stats call coalesces with the
 * Performance grid's TODAY call. Reads live; the test tenant shows $0 spend / `–` CPA until
 * a campaign delivers. Contract: `docs/api.owners-hub.acquisition-campaigns.md`.
 */
import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchStatsDataSetHistory,
  selectStatsDataSetHistory,
  selectStatsDataSetHistoryError,
  selectStatsDataSetHistoryLoading,
  type StatsHistoryRef,
} from '@/store/slices/ownerHubStats';

import { ALL_ACQUISITION_AFTS, isAcquisitionChannelEnabled } from '../../config/acquisitionChannels';
import { useAcquisitionChannelGate } from '../../hooks/useAcquisitionChannelGate';
import { homeWindow } from '../home/stats-mapping';
import { toBucketsFor } from '../home/stats-shape';
import { LIVE_REFRESH_MS, useRefreshTick } from '../home/useRefreshTick';
import { getAdCampaignPerformance, listAdCampaigns } from './api/casino-ad-service';
import { buildChannelFigure, type ChannelFigure } from './channel-figures';

export type { ChannelFigure } from './channel-figures';

const DATA_SET = 'daily_affiliate_activity';
const AGGREGATE_ON = ['aft'];
const DIMENSION = 'aft';

export type AcquisitionChannelFigures = {
  figures: Record<string, ChannelFigure>;
  loading: boolean;
};

type Spend = { byNetwork: Record<string, number>; errored: boolean; loading: boolean };
const SPEND_LOADING: Spend = { byNetwork: {}, errored: false, loading: true };

/**
 * @param tenantId Active brand tenant id (`displayBrand?.tenant_id`) for the Ad Service.
 * @param casinoInstanceId Active brand id (`displayBrand?.id`) for the Stats call.
 */
export const useAcquisitionChannelFigures = (
  tenantId: string | undefined,
  casinoInstanceId: string | undefined,
): AcquisitionChannelFigures => {
  const dispatch = useAppDispatch();
  const tick = useRefreshTick(true, LIVE_REFRESH_MS);
  // Reactive reveal flag: re-fetch spend + re-derive when a channel unlocks/locks at runtime.
  const { revealed } = useAcquisitionChannelGate();
  // UTC today: the same basis the Performance grid uses, so this `aft` Stats call shares its
  // cache entry, and the ad-spend `cost` covers the same calendar day.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const window = React.useMemo(() => homeWindow('TODAY', Date.now()), [tick]);
  const adToday = window.startDate.slice(0, 10);

  /* 1. Per-network ad-spend today: list once + per-campaign cost, grouped by network. */
  const [spend, setSpend] = React.useState<Spend>(SPEND_LOADING);
  const keyRef = React.useRef<string>('');
  React.useEffect(() => {
    if (!tenantId) {
      setSpend(SPEND_LOADING);
      keyRef.current = '';
      return;
    }
    const controller = new AbortController();
    const { signal } = controller;
    const key = `${tenantId}|${adToday}`;
    if (key !== keyRef.current) setSpend((prev) => ({ ...prev, loading: true }));
    keyRef.current = key;

    listAdCampaigns({ tenantId, pageSize: 100 }, signal)
      .then(async (list) => {
        // Only the live channels' campaigns matter (locked cards hide their figures).
        const live = list.campaigns.filter((campaign) => isAcquisitionChannelEnabled(campaign.network));
        const costs = await Promise.all(
          live.map((campaign) =>
            getAdCampaignPerformance(
              campaign.campaign_id,
              tenantId,
              { breakdown: 'summary', dateFrom: adToday, dateTo: adToday },
              signal,
            )
              .then((perf) => ({ network: campaign.network, cost: perf.totals.cost ?? 0 }))
              .catch(() => ({ network: campaign.network, cost: 0 })),
          ),
        );
        if (signal.aborted) return;
        const byNetwork: Record<string, number> = {};
        for (const { network, cost } of costs) byNetwork[network] = (byNetwork[network] ?? 0) + cost;
        setSpend({ byNetwork, errored: false, loading: false });
      })
      .catch(() => {
        if (!signal.aborted) setSpend({ byNetwork: {}, errored: true, loading: false });
      });

    return () => controller.abort();
  }, [tenantId, adToday, tick, revealed]);

  /* 2. FTDs per aft: ONE Stats call grouped on aft (today), read per channel. */
  const ready = Boolean(casinoInstanceId);
  const statsRef: StatsHistoryRef | undefined = ready
    ? {
        casinoInstanceId: casinoInstanceId as string,
        dataSet: DATA_SET,
        startDate: window.startDate,
        endDate: window.endDate,
        intervalSlice: window.intervalSlice,
        aggregateOn: AGGREGATE_ON,
      }
    : undefined;
  const statsEntry = useAppSelector((s) => selectStatsDataSetHistory(s, statsRef));
  const statsLoadingFlag = useAppSelector((s) => selectStatsDataSetHistoryLoading(s, statsRef));
  const statsError = useAppSelector((s) => selectStatsDataSetHistoryError(s, statsRef));
  React.useEffect(() => {
    if (!ready) return;
    dispatch(
      fetchStatsDataSetHistory({
        casinoInstanceId: casinoInstanceId as string,
        dataSet: DATA_SET,
        startDate: window.startDate,
        endDate: window.endDate,
        intervalSlice: window.intervalSlice,
        aggregateOn: [...AGGREGATE_ON],
      }),
    );
  }, [dispatch, ready, casinoInstanceId, window.startDate, window.endDate, window.intervalSlice, tick]);
  const statsLoading = ready && statsEntry === undefined && (statsLoadingFlag || statsError === undefined);

  // `revealed` is a real dependency: `isAcquisitionChannelEnabled` reads the reveal flag at
  // call time, so the derivation below must recompute when it flips. ESLint can't trace that
  // indirect read and flags it as unnecessary, hence the suppress on the deps array.
  const figures = React.useMemo<Record<string, ChannelFigure>>(() => {
    const out: Record<string, ChannelFigure> = {};
    for (const aft of ALL_ACQUISITION_AFTS) {
      if (!isAcquisitionChannelEnabled(aft)) continue;
      const cost = spend.byNetwork[aft] ?? 0;
      const ftdBuckets = statsEntry ? toBucketsFor(statsEntry.data, DIMENSION, aft) : [];
      out[aft] = buildChannelFigure(cost, ftdBuckets, spend.errored);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spend, statsEntry, revealed]);

  return { figures, loading: spend.loading || statsLoading };
};

export default useAcquisitionChannelFigures;
